import type { PositionData } from "@crypto-trading/common/browser";

type DrawerPosition = Pick<
  PositionData,
  "positionId" | "symbol" | "positionType" | "exchangeCode"
> & { liquidationPrice?: string; dbPositionId?: string };

const toPrice = (value: unknown): number | null => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const decimalsOf = (value: string | number): number | null => {
  const text = String(value).trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const fraction = text.split(".")[1]?.replace(/0+$/, "") ?? "";
  return fraction.length;
};

/**
 * Liquidation price as `$10.406`: the entry price's decimals (the exchange's
 * tick size, at least 2), and never more than 5 significant digits.
 */
export function formatLiquidationPrice(
  price: number,
  entryPrice?: string | number | null
): string {
  const integerDigits = Math.floor(Math.log10(price)) + 1;
  const significantDecimals = Math.max(0, 5 - integerDigits);
  const entryDecimals =
    entryPrice === null || entryPrice === undefined
      ? null
      : decimalsOf(entryPrice);
  const decimals =
    entryDecimals === null
      ? significantDecimals
      : Math.min(
          Math.max(entryDecimals, Math.min(2, significantDecimals)),
          significantDecimals
        );
  return `$${price.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Liquidation price for an open position, from the exchange's own value on
 * the position or, when the drawer was opened from a DB row (alert
 * positions), from open positions already in the react-query cache. Never
 * fetches anything.
 */
export function resolveLiquidationPrice(
  position: DrawerPosition,
  cachedOpenPositionLists: unknown[]
): number | null {
  const own = toPrice(position.liquidationPrice);
  if (own !== null) return own;

  const symbol = position.symbol.toUpperCase();
  const type = position.positionType.toUpperCase();
  const exchange = position.exchangeCode?.toLowerCase();
  for (const list of cachedOpenPositionLists) {
    if (!Array.isArray(list)) continue;
    for (const cached of list as DrawerPosition[]) {
      if (!cached || typeof cached !== "object") continue;
      const sameRow =
        cached.dbPositionId === position.positionId ||
        cached.positionId === position.positionId;
      const samePosition =
        cached.symbol?.toUpperCase() === symbol &&
        cached.positionType?.toUpperCase() === type &&
        (!exchange || cached.exchangeCode?.toLowerCase() === exchange);
      if (!sameRow && !samePosition) continue;
      const price = toPrice(cached.liquidationPrice);
      if (price !== null) return price;
    }
  }
  return null;
}
